import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BackdropCircular, Breadcrumb} from 'components';
import {
  Card,
  Grid,
  Typography,
  Fab,
  Icon,
  IconButton,
  Button,
  TextField,
  MenuItem,
  LinearProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { Formik } from 'formik';
import * as yup from 'yup';
import Autocomplete from '@material-ui/lab/Autocomplete';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import {
  getStoreCategoriesApi,
  uploadImageApi,
  saveRequestSkuEmailDataApi
} from 'api';
import { removeUploadedImage } from 'redux/actions/email';
import { uploadFileTypeUnsupported } from 'redux/actions/general';
import { useHistory } from 'react-router';


const useStyles = makeStyles(() => ({
  hint: {
    fontStyle: 'italic',
    color: '#122E3F',
    fontWeight: 400,
    fontSize: '12px'
  },
  maxWidthText: {
    maxWidth: '130px'
  },
}));

const treatmentTypes = [
  'Age Management', 'Active Acne', 'Age Spots/Sun Damage', 'Actinic Keratoses', 'Acne Scarring', 'Angiomas', 'Arthritis',
  'Athletic Injury or Pain', 'Back Acne', 'Basal Cell Carcinoma', 'Capillaries', 'Cellulite', 'Chronic Venus Insufficiency (CVI)',
  'Congested/Clogged Pores', 'Dark Circles', 'Decreased Energy', 'Decreased Libido', 'Deep and Superficial Lines', 'Dehydrated Skin',
  'Dehydration', 'Dull/Tired Skin', 'Exhaustion', 'Facial Folds', 'Facial Veins/Capillaries', 'Facial Volume Loss', 'Fine Lines', 'Hair Loss',
  'Hangover', 'Headaches/Migraines', 'Hormonal Imbalance', 'Hyperhidrosis/Sweating', 'Hyperpigmentation/Uneven Skintone', 'Hypopigmentation',
  'Illness or Injury', 'Immunity Boost', 'Inadequate/Thin Eyelashes', 'Incontinence', 'Ingrown Hairs/Razor Bumps', 'Large Pores', 'Melasma/Freckles',
  'Mobility Restoration', 'Moles', 'N/A', 'Nail Fungus', 'Neck or Spinal Pain', 'Neck Laxity', 'Onychomycosis', 'Orthopedic Conditions',
  'Pale or Thin Lips', 'Pain Relief', 'Perioral Lines', 'Port Wine Stains', 'Psoriasis', 'Rectal Tears', 'Rosacea/Redness', 'Sagging/Loose Skin',
  'Scars', 'Sexual Wellness', 'Skin Tags', 'Sleep Apnea', 'Snoring', 'Spider Veins', 'Spinal Misalignment', 'Squamous Cell Carcinoma', 'Stress',
  'Stretch Marks', 'Submental Fullness/Double Chin', 'Tattoos', 'Thin Lips', 'Thin or Inadequate Lashes', 'Thin or Light Eyebrows', 'TMJD',
  'Torn or Elongated Earlobes', 'Uneven Skin Texture', 'Unwanted Hair', 'Unwanted or Stubborn Fat', 'Urinary Incontinence', 'Varicose Veins',
  'Warts', 'Wrinkles'];

const validationSchema = yup.object({
  product_name: yup
    .string()
    .required('Field is required'),
  brand_name: yup
    .string()
    .required('Field is required'),
  product_price: yup
    .number()
    .positive()
    .min(0)
    .required('Field is required'),
  is_taxeble: yup
    .bool()
    .required('Field is required'),
  is_sold_online: yup
    .bool()
    .required('Field is required'),
  is_rx_required: yup
    .bool()
    .required('Field is required'),
  short_description: yup
    .string()
    .required('Field is required'),
  long_description: yup
    .string()
    .required('Field is required'),
  benefits: yup
    .string()
    .required('Field is required'),
  recomended_uses: yup
    .string()
    .required('Field is required'),
  faq: yup
    .string()
    .required('Field is required'),
  treatment_types: yup
    .array()
    .min(1, 'Field is required')
    .required('Field is required'),
  categories: yup
    .array()
    .min(1, 'Field is required')
    .required('Field is required')
});

const inputField = (label, name, handleChange, handleBlur, values, touched, errors) => {
  return (
    <TextField
      required
      fullWidth
      variant="outlined"
      type={name === 'product_price' ? 'number' : 'text'}
      label={label}
      name={name}
      value={values[name] || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      error={touched[name] && Boolean(errors[name])}
      helperText={touched[name] && errors[name]}
    />
  );
};

const inputFieldMultiline = (label, name, handleChange, handleBlur, values, touched, errors) => {
  return (
    <TextField
      multiline
      rows={5}
      required
      fullWidth
      variant="outlined"
      label={label}
      name={name}
      className="my-4"
      value={values[name] || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      error={touched[name] && Boolean(errors[name])}
      helperText={touched[name] && errors[name]}
    />
  );
};

const selectField = (label, name, handleChange, handleBlur, values, touched, errors) => {
  return (
    <TextField
      required
      fullWidth
      select
      variant="outlined"
      label={label}
      name={name}
      value={values[name] || ''}
      onChange={handleChange}
      onBlur={handleBlur}
      error={touched[name] && Boolean(errors[name])}
      helperText={touched[name] && errors[name]}
    >
      <MenuItem value="true">Yes</MenuItem>
      <MenuItem value="false">No</MenuItem>
    </TextField>
  );
};


const RequestSKU = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const locationState = history.location.state;
  const isFromQuoting = locationState && locationState.from.includes('quoting/listings');

  const [dargClass, setDragClass] = useState('');

  const {profile} = useSelector((state) => state.user);
  const {storeCategories, loadingCategories, storeCategoriesWithActiveNavLevel} = useSelector((state) => state.categories);
  const {uploadedImagesUrls, imageLoading, saveEmailDataLoading} = useSelector((state) => state.email);
  const loading = imageLoading || saveEmailDataLoading;

  const initialValues = {
    product_name: '',
    brand_name: '',
    product_price: '',
    is_taxeble: '',
    is_sold_online: '',
    is_rx_required: '',
    short_description: '',
    long_description: '',
    benefits: '',
    recomended_uses: '',
    faq: '',
    file: [],
    treatment_types: [],
    categories: []
  };

  useEffect(() => {
    if (storeCategories.length === 0) {
      getStoreCategoriesApi({dispatch});
    }
  }, []);

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragClass('drag-shadow');
  };

  const handleDragStart = () => setDragClass('drag-shadow');

  const handleSingleRemove = (index) => dispatch(removeUploadedImage(index));

  const isEmpty = !uploadedImagesUrls.length;

  const handleSubmit = (values, actions) => {
    const emailData = {
      ...values,
      is_taxeble: values.is_taxeble === 'true',
      is_sold_online: values.is_sold_online === 'true',
      is_rx_required: values.is_rx_required === 'true',
      file: uploadedImagesUrls
    };
    saveRequestSkuEmailDataApi({dispatch, emailData});
    actions.resetForm(initialValues);
  };

  return (
    <div className="flex-grow h-full min-h-full-screen scroll-y pb-25">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: isFromQuoting ? 'Quote Listings' : 'Listings', path: isFromQuoting ? '/quoting/listings/active' : '/store-products/list/active' },
            { name: 'Request Custom Listing' }
          ]}
        />
      </div>
      <div className="px-10">
        <Typography variant="body1" className={clsx('text-small', classes.hint)}>Please fill in the details below.</Typography>
        <Card className="p-8 mt-4" elevation={25}>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            enableReinitialize
            validationSchema={validationSchema}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue
            }) => (
              <form onSubmit={handleSubmit} noValidate>
                <Grid container spacing={5}>
                  <Grid item sm={12} md={4}>
                    <Typography variant="body1" className="text-brand text-16 mb-4">Product Image</Typography>
                    <div
                      className={clsx(
                        'h-300 w-full mb-6 flex-column justify-center items-center dropzone',
                        dargClass
                      )}
                      onDragEnter={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.persist();
                        const {files} = event.dataTransfer;
                        const uploadedFiles = Object.values(files).filter((file) => file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/jpeg');

                        if (uploadedFiles.length !== 0) {
                          const formData = new FormData();
                          formData.append('image', files[0]);
                          uploadImageApi({dispatch, image: formData});
                        } else {
                          dispatch(uploadFileTypeUnsupported());
                        }

                        setDragClass('');
                        return false;
                      }}
                    >
                      <div>
                        <Icon className="pr-8 text-48" fontSize="large" color="primary">cloud_upload</Icon>
                      </div>

                      <span className="font-bold text-16 text-brand">Drag and drop your file here</span>
                      <span className="text-13 text-muted">Files supported: JPG and PNG</span>
                      <p className="text-13 text-brand my-2">or</p>
                      <label htmlFor="upload-image">
                        <Fab
                          component="span"
                          variant="extended"
                          className="border-radius-30 capitalize bg-white text-13 h-30px text-primary font-bold border-1-primary box-shadow-none"
                        >
                          Browse file
                        </Fab>
                      </label>
                      <input
                        className="hidden"
                        onClick={(event) => event.target.value = null}
                        onChange={(event) => {
                          const {files, value} = event.target;
                          const formData = new FormData();

                          const fileTypeDotIndexPosition = value.lastIndexOf('.') + 1;
                          const slicedFileTypeFromFilePath = value.slice(fileTypeDotIndexPosition);
                          // image validation regex
                          const acceptableFormats = 'png|jpeg';
                          const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

                          if (!hasImageFile.test(files[0].type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
                            dispatch(uploadFileTypeUnsupported());
                          } else {
                            formData.append('image', files[0]);
                            uploadImageApi({dispatch, image: formData});
                          }
                        }}
                        id="upload-image"
                        type="file"
                        accept="image/png, image/jpeg"
                      />
                    </div>
                    {!isEmpty &&
                    <div>
                      <p>Uploaded Files:</p>
                      {uploadedImagesUrls.length !== 0 && uploadedImagesUrls.map((imageUrl, index) => {
                        return (
                          <div key={index} className="flex justify-between items-center">
                            <span className="pr-2 text-16">{index + 1}. </span>
                            <img src={imageUrl} alt="Uploaded Image" className="max-w-80full py-2" />
                            <IconButton onClick={() => handleSingleRemove(index)}>
                              <Icon>delete</Icon>
                            </IconButton>
                          </div>
                        );
                      })}
                    </div>
                    }
                  </Grid>
                  <Grid item sm={12} md={8}>
                    <Grid container spacing={3} className="my-3">
                      <Grid item xs={12} className={clsx('pr-0', classes.maxWidthText)} >
                        <Typography variant="body1" className="text-brand text-16 mb-4">
                          Store Name:
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={10} className="pl-0">
                        <TextField
                          fullWidth
                          className="text-brand text-16 mb-4"
                          id="outlined-read-only-input"
                          value={profile.stores?.[0].storeUrl}
                          InputProps={{
                            readOnly: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Typography variant="body1" className="text-brand text-16 mb-4">Product Information</Typography>
                    {inputField('Product Name', 'product_name', handleChange, handleBlur, values, touched, errors)}
                    <Grid container spacing={3} className="my-5">
                      <Grid item xs={12} md={6}>
                        {inputField('Brand', 'brand_name', handleChange, handleBlur, values, touched, errors)}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {inputField('Price', 'product_price', handleChange, handleBlur, values, touched, errors)}
                      </Grid>
                    </Grid>
                    <Grid container spacing={3} className="mb-2">
                      <Grid item xs={12} md={4}>
                        {selectField('Taxable?', 'is_taxeble', handleChange, handleBlur, values, touched, errors)}
                      </Grid>
                      <Grid item xs={12} md={4}>
                        {selectField('Sold Online?', 'is_sold_online', handleChange, handleBlur, values, touched, errors)}
                      </Grid>
                      <Grid item xs={12} md={4}>
                        {selectField('RX Required?', 'is_rx_required', handleChange, handleBlur, values, touched, errors)}
                      </Grid>
                    </Grid>
                    {inputFieldMultiline('Short Description', 'short_description', handleChange, handleBlur, values, touched, errors)}
                    {inputFieldMultiline('Long Description', 'long_description', handleChange, handleBlur, values, touched, errors)}
                    {inputFieldMultiline('Benefits', 'benefits', handleChange, handleBlur, values, touched, errors)}
                    {inputFieldMultiline('Recommended Uses', 'recomended_uses', handleChange, handleBlur, values, touched, errors)}
                    {inputFieldMultiline('FAQs', 'faq', handleChange, handleBlur, values, touched, errors)}

                    <Autocomplete
                      multiple
                      fullWidth
                      disableClearable
                      className="my-4"
                      value={values.treatment_types}
                      options={treatmentTypes}
                      onChange={(e, value) => {setFieldValue('treatment_types', value !== null ? value : values.treatment_types);}}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          required
                          name="treatment_types"
                          variant="outlined"
                          label="Treatment Type or Treatment Problem"
                          error={touched.treatment_types && Boolean(errors.treatment_types)}
                          helperText={touched.treatment_types && errors.treatment_types}
                        />
                      )}
                      renderOption={(option, { inputValue }) => {
                        const matches = match(option, inputValue);
                        const parts = parse(option, matches);
                        return (
                          <div>
                            {parts.map((part, index) => (
                              <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                                {part.text}
                              </span>
                            ))}
                          </div>
                        );
                      }}
                    />
                    {loadingCategories ?
                      <div className="my-10" >
                        Preparing Categories Data...
                        <LinearProgress />
                      </div>
                      :
                      <Autocomplete
                        multiple
                        fullWidth
                        disableClearable
                        className="my-8"
                        value={values.categories}
                        options={storeCategoriesWithActiveNavLevel}
                        getOptionLabel={(option) => option.nameWithPath}
                        onChange={(e, value) => {setFieldValue('categories', value !== null ? value : values.categories);}}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            name="categories"
                            variant="outlined"
                            label="Categories"
                            required
                            error={touched.categories && Boolean(errors.categories)}
                            helperText={touched.categories && errors.categories}
                          />
                        )}
                        renderOption={(option, { inputValue }) => {
                          const matches = match(option?.nameWithPath, inputValue);
                          const parts = parse(option?.nameWithPath, matches);
                          return (
                            <div>
                              {parts.map((part, index) => (
                                <span key={index} className={`${part.highlight ? 'text-primary font-bold' : ''}`}>
                                  {part.text}
                                </span>
                              ))}
                            </div>
                          );
                        }}
                      />}
                  </Grid>
                </Grid>
                <div className="flex justify-end uppercase">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="medium"
                    className="uppercase"
                    disabled={loadingCategories}
                  >
                    <Icon className="mr-2">send</Icon>
                    Submit
                  </Button>
                </div>
              </form>
            )}
          </Formik>

        </Card>
      </div>
      <BackdropCircular loading={loading} />
    </div>
  );
};

export default RequestSKU;
